import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import { fetchWithAuth } from "@/hooks/useAuth";
import { useNomenclator } from "@/hooks/useNomeclator";
import { Order } from "@/prisma-types";
import { useQueryClient } from "@tanstack/react-query";
import { Building2, Home, MapPin, Pencil, Save, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";


function OrderAddressContainer({ order }: { order: Order }) {
  const isLocker = Boolean(order.metadata?.lockerId);
  const queryClient = useQueryClient();
  const {
    counties,
    cities,
    getCitiesForCounty,
    isLoading: isLoadingNomenclator,
  } = useNomenclator();
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState({
    judet: order.judet || "",
    localitate: order.localitate || "",
    strada: order.strada || "",
    numar: order.numar || "",
    bloc: order.bloc || "",
    scara: order.scara || "",
    apartament: order.apartament || "",
  });

  // Ensure cities for initial county are loaded
  const selectedCounty = useMemo(
    () => counties.find((c: any) => c.name === (form.judet || order.judet)),
    [counties, form.judet, order.judet]
  ) as any;
  const availableCities = useMemo(
    () => (selectedCounty ? cities[selectedCounty.id] || [] : []),
    [cities, selectedCounty]
  );

  useEffect(() => {
    if (selectedCounty && !cities[selectedCounty.id]) {
      getCitiesForCounty(selectedCounty.id);
    }
  }, [selectedCounty, cities, getCitiesForCounty]);

  useEffect(() => {
    if (isEditing) {
      setForm({
        judet: order.judet || "",
        localitate: order.localitate || "",
        strada: order.strada || "",
        numar: order.numar || "",
        bloc: order.bloc || "",
        scara: order.scara || "",
        apartament: order.apartament || "",
      });
    }
  }, [isEditing, order]);

  const handleCountyChange = async (countyName: string) => {
    setForm((prev) => ({ ...prev, judet: countyName, localitate: "" }));
    const county = counties.find((c: any) => c.name === countyName) as any;
    if (county && !cities[county.id]) {
      await getCitiesForCounty(county.id);
    }
  };

  const handleCityChange = (cityName: string) => {
    setForm((prev) => ({ ...prev, localitate: cityName }));
  };

  const onSave = async () => {
    const { judet, localitate, strada, numar, bloc, scara, apartament } = form;
    if (!judet || !localitate || !strada || !numar) {
      toast.error("Completează județ, localitate, strada și număr");
      return;
    }
    try {
      const res = await fetchWithAuth(`/api/admin/orders/${order.id}/address`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          judet,
          localitate,
          strada,
          numar,
          bloc: bloc || null,
          scara: scara || null,
          apartament: apartament || null,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.message || "Eroare la actualizarea adresei");
      }
      const updatedOrder = await res.json();
      // Update and refresh order
      await Promise.all([
        (async () => {
          // Update both numeric and string key variants for safety
          queryClient.setQueryData(["order", order.id], updatedOrder);
          queryClient.setQueryData(["order", String(order.id)], updatedOrder);
        })(),
        queryClient.invalidateQueries({ queryKey: ["orders"] }),
        queryClient.invalidateQueries({ queryKey: ["order"] }),
      ]);
      toast.success("Adresa a fost actualizată");
      setIsEditing(false);
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : "Eroare la actualizarea adresei"
      );
    }
  };

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-semibold flex items-center gap-2">
          <MapPin className="w-5 h-5 text-muted-foreground" />
          Adresă Livrare
        </h2>
        {!isEditing ? (
          <Button
            variant="outline"
            disabled={isLocker}
            size="sm"
            onClick={() => setIsEditing(true)}
            className="flex items-center gap-2"
          >
            <Pencil className="w-4 h-4" /> Editează
          </Button>
        ) : (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsEditing(false)}
              className="flex items-center gap-2"
            >
              <X className="w-4 h-4" /> Renunță
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={onSave}
              className="flex items-center gap-2"
            >
              <Save className="w-4 h-4" /> Salvează
            </Button>
          </div>
        )}
      </div>

      {!isEditing ? (
        <div className={`space-y-2 ${isLocker ? "hidden" : ""}`}>
          <div className="flex justify-between">
            <span className="text-muted-foreground flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              Județ:
            </span>
            <span>{order.judet}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground flex items-center gap-2">
              <Home className="w-4 h-4" />
              Localitate:
            </span>
            <span>{order.localitate}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Adresă:
            </span>
            <span>
              {order.strada} {order.numar}
              {order.bloc && `, Bl. ${order.bloc}`}
              {order.scara && `, Sc. ${order.scara}`}
              {order.apartament && `, Ap. ${order.apartament}`}
            </span>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-muted-foreground mb-1">
                Județ
              </label>
              <Select value={form.judet} onValueChange={handleCountyChange}>
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      isLoadingNomenclator
                        ? "Se încarcă..."
                        : "Selectează județul"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {counties.map((c: any) => (
                    <SelectItem key={c.id} value={c.name}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm text-muted-foreground mb-1">
                Localitate
              </label>
              <Select
                value={form.localitate}
                onValueChange={handleCityChange}
                disabled={!form.judet || isLoadingNomenclator}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      !form.judet
                        ? "Selectează județul mai întâi"
                        : "Selectează localitatea"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {availableCities.map((city: any) => (
                    <SelectItem key={city.id} value={city.name}>
                      {city.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-muted-foreground mb-1">
                Strada
              </label>
              <Input
                value={form.strada}
                onChange={(e) =>
                  setForm((p) => ({ ...p, strada: e.target.value }))
                }
              />
            </div>
            <div>
              <label className="block text-sm text-muted-foreground mb-1">
                Număr
              </label>
              <Input
                value={form.numar}
                onChange={(e) =>
                  setForm((p) => ({ ...p, numar: e.target.value }))
                }
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-sm text-muted-foreground mb-1">
                Bloc
              </label>
              <Input
                value={form.bloc}
                onChange={(e) =>
                  setForm((p) => ({ ...p, bloc: e.target.value }))
                }
              />
            </div>
            <div>
              <label className="block text-sm text-muted-foreground mb-1">
                Scara
              </label>
              <Input
                value={form.scara}
                onChange={(e) =>
                  setForm((p) => ({ ...p, scara: e.target.value }))
                }
              />
            </div>
            <div>
              <label className="block text-sm text-muted-foreground mb-1">
                Apartament
              </label>
              <Input
                value={form.apartament}
                onChange={(e) =>
                  setForm((p) => ({ ...p, apartament: e.target.value }))
                }
              />
            </div>
          </div>
        </div>
      )}

      {isLocker && (
        <div className="space-y-3">
          <div className="flex gap-2 text-blue-500">
            <span className=" flex items-center gap-2">Locker:</span>
            <span>{order.metadata?.lockerName}</span>
          </div>
        </div>
      )}
    </Card>
  );
}

export default OrderAddressContainer;
